import { useState } from "react";
import { AuthProvider, useAuth, useRole } from "@/lib/auth";
import { getLang, setLang as setLangStore, t } from "@/lib/i18n";
import {
  GraduationCap, MapPin, Globe, UserPlus, Award, HandHeart,
  Send, Sliders, School, CalendarDays, LogOut, Menu, X, LayoutDashboard, Users,
} from "lucide-react";
import HomePage from "@/components/HomePage";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import StudentRegistration from "@/components/StudentRegistration";
import Academics from "@/components/Academics";
import Management from "@/components/Management";
import Dashboard from "@/components/Dashboard";
import AttendanceDashboard from "@/components/AttendanceDashboard";
import StudentManagement from "@/components/StudentManagement";
import PublicSections from "@/components/PublicSections";

type TabId =
  | "registration" | "certificates" | "caravana" | "messaging"
  | "admin" | "schoolManagement" | "attendance" | "dashboard" | "studentManagement";

type AttendanceTarget = {
  classId: string;
  level: "preschool" | "primary" | "secondary";
  shift: "morning" | "afternoon";
  schoolId?: string;
} | null;

function AppContent() {
  const { loading, profile } = useAuth();
  const { isAdmin, isStaff, isTeacher, isStaffOrAdmin } = useRole();
  const [activeTab, setActiveTab] = useState<TabId>("registration");
  const [lang, setLangState] = useState(getLang());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Bridges "Take Attendance" from Student Management straight into the
  // matching class in Academics, instead of the user re-filtering twice.
  const [attendanceTarget, setAttendanceTarget] = useState<AttendanceTarget>(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#38B6FF] border-t-[#1C3988] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1C3988] font-semibold">{t("loading_portal")}</p>
        </div>
      </div>
    );
  }

  // Not logged in -> show auth page
  if (!profile) {
    return <HomePage />;
  }

  // Must change password on first login
  if (profile.must_change_password) {
    return <ChangePasswordModal />;
  }

  const changeLanguage = (l: string) => {
    setLangStore(l as any);
    setLangState(l as any);
  };

  const { signOut } = useAuth();

  // Build nav items based on role
  type NavItem = { id: TabId; label: string; icon: React.ReactNode; show: boolean };
  const navItems: NavItem[] = [
    { id: "registration", label: t("nav_reg"), icon: <UserPlus className="w-4 h-4" />, show: isStaffOrAdmin },
    { id: "studentManagement", label: "Students", icon: <Users className="w-4 h-4" />, show: isStaffOrAdmin },
    { id: "schoolManagement", label: t("nav_school_mgmt"), icon: <School className="w-4 h-4" />, show: isStaffOrAdmin },
    { id: "dashboard", label: t("nav_dashboard"), icon: <LayoutDashboard className="w-4 h-4" />, show: isAdmin },
    { id: "attendance", label: t("nav_attendance"), icon: <CalendarDays className="w-4 h-4" />, show: isAdmin },
    { id: "admin", label: t("nav_admin"), icon: <Sliders className="w-4 h-4" />, show: isAdmin },
    { id: "certificates", label: t("nav_certs"), icon: <Award className="w-4 h-4" />, show: true },
    { id: "caravana", label: t("nav_caravana"), icon: <HandHeart className="w-4 h-4" />, show: true },
    { id: "messaging", label: t("nav_contact"), icon: <Send className="w-4 h-4" />, show: true },
  ];

  const visibleNav = navItems.filter((n) => n.show);

  // If active tab is not visible to current role, switch to first visible
  const currentTabVisible = visibleNav.some((n) => n.id === activeTab);
  const effectiveTab = currentTabVisible ? activeTab : visibleNav[0]?.id || "certificates";

  const renderTab = () => {
    switch (effectiveTab) {
      case "registration": return <StudentRegistration />;
      case "studentManagement": return (
        <StudentManagement
          onTakeAttendance={(target) => {
            setAttendanceTarget(target);
            setActiveTab("schoolManagement");
          }}
        />
      );
      case "schoolManagement": return (
        <Academics
          initialAttendanceTarget={attendanceTarget}
          onConsumeInitialTarget={() => setAttendanceTarget(null)}
        />
      );
      case "dashboard": return <Dashboard />;
      case "attendance": return <AttendanceDashboard />;
      case "admin": return <Management />;
      case "certificates": return <PublicSections tab="certificates" />;
      case "caravana": return <PublicSections tab="caravana" />;
      case "messaging": return <PublicSections tab="messaging" />;
      default: return <PublicSections tab="certificates" />;
    }
  };

  const roleLabel =
    profile.role === "admin" ? t("role_admin")
    : profile.role === "staff" ? t("role_staff")
    : profile.role === "teacher" ? t("role_teacher")
    : t("role_guardian");

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-gradient-to-br from-[#1C3988] to-[#122554] text-white px-5 py-5 flex items-center justify-between flex-wrap gap-4 shadow-lg">
        <div className="flex items-center gap-5">
          <div className="w-[75px] h-[75px] bg-white rounded-full flex items-center justify-center border-[3px] border-[#38B6FF] shadow-md flex-shrink-0">
            <GraduationCap className="w-10 h-10 text-[#1C3988]" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-wider">
              NATION <span className="text-[#38B6FF]">UBUNTU</span> SCHOOL
            </h1>
            <p className="text-sm text-[#E2E8F0] mt-1 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {t("brand_loc")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* User badge */}
          <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg border border-[#38B6FF]/30">
            <div className="w-8 h-8 bg-[#7A2222] rounded-full flex items-center justify-center text-sm font-bold">
              {profile.full_name.charAt(0).toUpperCase()}
            </div>
            <div className="text-sm">
              <p className="font-semibold">{profile.full_name}</p>
              <p className="text-[#38B6FF] text-xs">{roleLabel}</p>
            </div>
          </div>

          {/* Language selector */}
          <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg border border-[#38B6FF]/30">
            <Globe className="w-4 h-4 text-[#38B6FF]" />
            <select
              value={lang}
              onChange={(e) => changeLanguage(e.target.value)}
              className="bg-[#7A2222] text-white border border-[#38B6FF] px-2 py-1 rounded-md font-semibold cursor-pointer outline-none text-sm"
            >
              <option value="en">{t("lang_en")}</option>
              <option value="fr">{t("lang_fr")}</option>
              <option value="pt">{t("lang_pt")}</option>
            </select>
          </div>

          {/* Sign out */}
          <button
            onClick={() => signOut()}
            className="flex items-center gap-2 bg-[#7A2222] hover:bg-[#5c1919] px-3 py-2 rounded-lg font-semibold text-sm transition-all cursor-pointer"
          >
            <LogOut className="w-4 h-4" /> {t("nav_logout")}
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-[#7A2222] text-white overflow-x-auto">
        <div className="max-w-[1200px] mx-auto flex items-center px-5">
          {/* Mobile menu toggle */}
          <button
            className="lg:hidden py-4 mr-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <div className={`${mobileMenuOpen ? "flex-col absolute left-0 right-0 top-full bg-[#7A2222] z-50 shadow-lg" : "flex"} flex-1`}>
            {visibleNav.map((item) => (
              <button
                key={item.id}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold whitespace-nowrap transition-all hover:bg-white/15 border-b-4 ${
                  effectiveTab === item.id ? "border-[#38B6FF] bg-white/15" : "border-transparent"
                }`}
                onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
              >
                {item.icon} {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div className="flex-1 max-w-[1200px] w-full mx-auto px-5 py-8">
        {renderTab()}
      </div>

      {/* Footer */}
      <footer className="bg-[#1C3988] text-white text-center py-6 text-sm border-t-4 border-[#7A2222] mt-auto">
        <p>{t("footer_copy")}</p>
        <p><span className="text-[#38B6FF] font-semibold">{t("footer_collab")}</span> Fraternity Without Borders</p>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
